import {  useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';

import { InstantSearch } from 'react-instantsearch'; 
import { useTranslation } from '@klv/hooks/useTranslation';
import { searchClient } from '@klv/utils/search-client';

import { generateClassName } from '@klv/shared/utils/functions';
import useIsHydrated from '@klv/hooks/useIsHydrated';
import SearchBox from '@klv/molecules/search-box';
import InstantResults from '@klv/shared/search/instant-results';

import { Wrapper } from '@klv/utils/wrapper';

import * as styles from './styles/searchBar.module.scss';

import type { ISearchBar } from './types';
import type { ICustomPadding } from '@klv/atoms/custom-padding/types';

export const SearchBar = ({
  className,
  customPadding,
  postType,
  fadeIn = false,
  ...rest
}: ISearchBar) => {
  const { t } = useTranslation();
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const isHydrated = useIsHydrated();

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (searchBoxRef.current) {
        const searchBoxRect = searchBoxRef.current.getBoundingClientRect();
        setIsSticky(searchBoxRect.top < 120 && window.scrollY > 0);
      } else {
        setIsSticky(false);
      }
    }, 10);
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={searchBoxRef} className={styles.container}>
      <Wrapper
        className={generateClassName([className, styles.blogSearchBar])}
        grid={true}
        inlinePadding={true}
        fadeIn={fadeIn ? .25 : 0}
        customPadding={isSticky ? { customPaddingBottom: 'sm', customPaddingTop: 'sm' } : customPadding as ICustomPadding}
        customBackgroundColor={isSticky ? 'var(--color-charcoal-mid-tint)' : ''}
        {...rest}
      >
        {isHydrated && (
          <div className={styles.blogSearchBarContent}>
            <InstantSearch
              indexName="marketing_sites" 
              searchClient={searchClient}
              insights
            >
              <SearchBox
                selectFacets={[postType]}
                defaultIndex="marketing_sites"
                className={styles.searchBox}
                placeholder={t(`organisms_searchBar_placeholder_${postType}`)}
                instantResults={(
                  <InstantResults
                    filterIndex="marketing_sites"
                    customHeadline={t(`pages_search_refinements_klaviyoHome_postType_${postType}`)}
                    customFilter={`AND post_type:"${postType}"`}
                  />
                )}
              />
            </InstantSearch>
          </div>
        )}
      </Wrapper>
    </div>
  );
};
